import { useEffect, useRef } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { api } from './api';
import { UUID } from '~/Routes';
import { withSkin, extractSkinFromPath } from './router/solutionUrl';
import qs from 'qs';

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;
const HEARTBEAT_MS = 60_000;
const IDLE_AFTER_MS = 60_000;

type ActivityPayload = {
  solution: string;
  url: string;
  order?: string;
  trade?: string;
  tender?: string;
};

export default function Tracking() {
  const solution = useStoreState((state: any) => state.auth.solution);
  // The backend refuses every activity post until the terms are accepted.
  const termsPending = useStoreState(
    (state: any) => state.auth.privacyCheckReq
  );
  const checkAuth = useStoreActions((actions: any) => actions.auth.checkAuth);
  const history = useHistory();
  const location = useLocation();
  const orderMatch = useRouteMatch<{ id: string }>(
    withSkin(`/orders/:id(${UUID})`)
  );
  const tradeMatch = useRouteMatch<{ id: string }>(
    withSkin(`/trades/:id(${UUID})`)
  );
  const tenderMatch = useRouteMatch<{ id: string }>(
    withSkin(`/tenders/:id(${UUID})`)
  );

  const lastActive = useRef(Date.now());

  const buildPayloadRef = useRef<() => ActivityPayload | null>(() => null);
  buildPayloadRef.current = () => {
    if (!solution || termsPending) return null;
    const { pathname, search } = location;
    const data: ActivityPayload = {
      solution: solution.id,
      url: `${pathname}${search}`,
    };
    if (orderMatch) data.order = orderMatch.params.id;
    if (tradeMatch) data.trade = tradeMatch.params.id;
    if (tenderMatch) data.tender = tenderMatch.params.id;
    // Marketplace order views arrive as `?order=<id>` on the marketplace root,
    // which is `/` bare or `/<skin>` on a solution-skinned URL. Strip the skin
    // before checking for root so skinned buyers aren't dropped (NUI-4799).
    if (extractSkinFromPath(pathname).path === '/' && search) {
      const parsed = qs.parse(search.slice(1));
      if (typeof parsed.order === 'string') data.order = parsed.order;
    }
    return data;
  };

  const sendRef = useRef<() => Promise<void>>(async () => {});
  sendRef.current = async () => {
    const data = buildPayloadRef.current();
    if (!data) return;
    try {
      const response = await api.userActivity(data);
      if (response.status === 403 && response.headers?.reason) {
        if (history.location.pathname !== '/signin') {
          // Awaited so a rejection is swallowed by the catch below instead
          // of leaking as an unhandled rejection (FRAICHE-A).
          await checkAuth({ redirect: false });
        }
      }
    } catch {
      // swallow — transient network errors shouldn't break the heartbeat
    }
  };

  // Route-change post: fires once on solution ready, on every navigation, and
  // when the terms are accepted. Keyed on the id because checkAuth hands back a
  // new copy of the same solution, and re-posting on that copy turned a refused
  // post into a loop.
  const solutionId = solution?.id;
  useEffect(() => {
    if (!solutionId) return;
    void sendRef.current();
  }, [solutionId, termsPending, location.pathname, location.search]);

  // Bump last-active timestamp on real user input. useRef -> no re-renders.
  useEffect(() => {
    const bump = () => {
      lastActive.current = Date.now();
    };
    const opts = { passive: true } as const;
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, opts));
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
    };
  }, []);

  // Heartbeat: at most one POST per window, skipped when idle or tab hidden.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActive.current >= IDLE_AFTER_MS) return;
      void sendRef.current();
    };
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  return null;
}
